import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane
} from 'reactstrap';
import {IDriver} from 'app/shared/model/driver.model';
import {IAmbulance} from 'app/shared/model/ambulance.model';
import {IJourney} from 'app/shared/model/journey.model';
import classnames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getMMamaSettings} from "app/modules/administration/mmama-settings/mmama-settings.reducer";
import {createEntity as createRoute} from "app/entities/route/route.reducer";
import {getAllByType as getCountries} from "app/entities/region/region.reducer";
import {hasAnyPermission} from "app/shared/auth/permissions";
import {IMMamaSettings} from "app/shared/model/mmama-settings-model";
import driverSuggestionService from 'app/entities/driver/driver-suggestion.service';
import ambulanceService from 'app/entities/ambulance/ambulance.service';
import SearchDriverModal from './search-driver-modal';
import AddDriverModal from './add-driver-modal';
import AddAmbulanceModal from './add-ambulance-modal';
import RRDriverModal from './rr-driver-modal';
import RRAmbulanceModal from './rr-ambulance-modal';
import {JourneyDriverType} from "app/entities/dispatcher-dashboard/components/journey-info";
import {
  getEntitiesByPatientCase as getRejectedDriversByPatientCase
} from 'app/entities/rejection-reason/patient-case-rr-driver.reducer';
import {getEntities as getRejectionReasonsDriver} from 'app/entities/rejection-reason/rejection-reason.reducer';
import {
  getEntitiesByPatientCase as getRejectedAmbulancesByPatientCase
} from 'app/entities/rejection-reason-ambulance/patient-case-rr-ambulance.reducer';
import {getEntities as getRejectionReasonsAmbulance} from 'app/entities/rejection-reason-ambulance/rejection-reason.reducer';
import {fromCents} from "app/shared/util/money-utils";
import {round} from "lodash";
import {decompressImage} from "app/shared/util/image-util";
import {IDriverCompact} from "app/shared/model/driver-compact.model";
import {IAmbulanceCompact} from "app/shared/model/ambulance-compact.model";
import {calculatePaymentCost} from "app/entities/payment/payment-util";

interface DriverSelectionModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (driverType: JourneyDriverType, selectedDriver: IDriver | IAmbulance, totalDistance: number, totalDurationSeconds: number, totalCost: number) => void;
  journey: IJourney;
  fromLocation?: string;
  toLocation?: string;
}

const DriverSelectionModalComponent = (props: DriverSelectionModalProps) => {
  const {
    isOpen,
    toggle,
    onSave,
    journey,
    fromLocation,
    toLocation,
    mmamaSettings,
    rejectedDrivers,
    rejectionReasonsDriver,
    rejectedAmbulances,
    rejectionReasonsAmbulance
  } = props;

  // State
  const [driverType, setDriverType] = useState<JourneyDriverType>(journey.startVillage ? JourneyDriverType.COMMUNITY : JourneyDriverType.AMBULANCE);
  const [selectedDriver, setSelectedDriver] = useState<IDriver | IAmbulance | null>(null);
  const [countrySettings, setCountrySettings] = useState<IMMamaSettings | null>(null);
  const [communityDrivers, setCommunityDrivers] = useState<IDriver[]>([]);
  const [ambulanceDrivers, setAmbulanceDrivers] = useState<IAmbulance[]>([]);
  const [loadingCommunityDrivers, setLoadingCommunityDrivers] = useState(false);
  const [loadingAmbulanceDrivers, setLoadingAmbulanceDrivers] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [showAddAmbulanceModal, setShowAddAmbulanceModal] = useState(false);
  const [showRRDriverModal, setShowRRDriverModal] = useState(false);
  const [rejectionDriverId, setRejectionDriverId] = useState<string>('');
  const [showRRAmbulanceModal, setShowRRAmbulanceModal] = useState(false);
  const [rejectionAmbulanceId, setRejectionAmbulanceId] = useState<string>('');
  const [selectedDriverCost, setSelectedDriverCost] = useState<number | null>(null);
  const [selectedDriverDuration, setSelectedDriverDuration] = useState<number | null>(null);
  const [selectedDriverDistance, setSelectedDriverDistance] = useState<number | null>(null);
  const [calculatingCost, setCalculatingCost] = useState(false);
  const [driversLoaded, setDriversLoaded] = useState(false);

  const patientCountryId = journey?.patientCase?.patient?.countryId;

  // Helper function to check if a driver is rejected
  const isDriverRejected = (driverId: string): boolean => {
    if (!rejectedDrivers || !driverId) {
      return false;
    }
    return rejectedDrivers.some(rejection =>
      rejection.driverId === driverId || rejection.driver?.id === driverId
    );
  };

  const getRequiredVehicleLevel = (motherSymptoms, babySymptoms) => {
    const requiredLevels = [
      ...motherSymptoms.map(symptom => symptom.requiredVehicleLevel),
      ...babySymptoms.map(symptom => symptom.requiredVehicleLevel)
    ];

    return requiredLevels.length > 0 ? Math.max(...requiredLevels) : 1;
  };

  // Helper function to get rejection reason for a driver
  const getDriverRejectionReason = (driverId: string): string | null => {
    if (!rejectedDrivers || !driverId) {
      return null;
    }
    const rejection = rejectedDrivers.find(r =>
      r.driverId === driverId || r.driver?.id === driverId
    );

    const rrDriver = rejectionReasonsDriver?.find(rr => rr.id === rejection?.rrDriverId);
    const reasonText = `${rrDriver ? rrDriver.enRejectionReason : ''}${rejection?.description ? ` - ${rejection.description}` : ''}`;
    return reasonText !== '' ? reasonText : 'No reason provided';
  };

  // Helper function to check if an ambulance is rejected
  const isAmbulanceRejected = (ambulanceId: string): boolean => {
    if (!rejectedAmbulances || !ambulanceId) {
      return false;
    }
    return rejectedAmbulances.some(rejection =>
      rejection.ambulanceId === ambulanceId || rejection.ambulance?.id === ambulanceId
    );
  };

  // Helper function to get rejection reason for an ambulance
  const getAmbulanceRejectionReason = (ambulanceId: string): string | null => {
    if (!rejectedAmbulances || !ambulanceId) {
      return null;
    }
    const rejection = rejectedAmbulances.find(r =>
      r.ambulanceId === ambulanceId || r.ambulance?.id === ambulanceId
    );

    const rrAmbulance = rejectionReasonsAmbulance?.find(rr => rr.id === rejection?.rrAmbulanceId);
    const reasonText = `${rrAmbulance ? rrAmbulance.enRejectionReason : ''}${rejection?.description ? ` - ${rejection.description}` : ''}`;
    return reasonText !== '' ? reasonText : 'No reason provided';
  };

  // When showRRDriverModal closes fetch the rejected drivers again
  useEffect(() => {
    if (!showRRDriverModal) {
      if (journey?.patientCase?.id) {
        props.getRejectedDriversByPatientCase(journey.patientCase.id);
      }
    }
  }, [showRRDriverModal]);

  // When showRRAmbulanceModal closes fetch the rejected ambulances again
  useEffect(() => {
    if (!showRRAmbulanceModal) {
      if (journey?.patientCase?.id) {
        props.getRejectedAmbulancesByPatientCase(journey.patientCase.id);
      }
    }
  }, [showRRAmbulanceModal]);

  // Main cost calculation function - delegates to shared utility
  const calculateCost = async (driver) => {
    return await calculatePaymentCost(journey, driver, driverType, countrySettings, (routeData) => props.createRoute(routeData));
  };

  // Handle driver selection
  const handleDriverSelection = async (driver: IDriver | IAmbulance) => {
    setSelectedDriver(driver);

    // Calculate cost for the selected driver
    setCalculatingCost(true);
    try {
      const result = await calculateCost(driver);
      if (!driver.vehicleType?.paymentRequired) {
        setSelectedDriverCost(0);
      } else {
        setSelectedDriverCost(result.cost);
      }
      setSelectedDriverDistance(result.distance);
      setSelectedDriverDuration(result.duration);
    } catch (error) {
      console.error('Error calculating cost for selected driver:', error);
      alert(error.message || 'An error occurred while calculating the cost. Please try again.');
      setSelectedDriver(null);
      setSelectedDriverCost(0);
      setSelectedDriverDistance(0);
      setSelectedDriverDuration(0);
    } finally {
      setCalculatingCost(false);
    }
  };

  // Get formatted cost display for selected driver
  const getSelectedDriverCostDisplay = (): string => {
    if (!selectedDriver) return '';
    if (calculatingCost) return 'Calculating...';
    if (selectedDriverCost === null) return 'Cost not available';
    return `${round(fromCents(selectedDriverCost), 2)} ${countrySettings?.currencySymbol || ''}`;
  };

  useEffect(() => {
    if (isOpen) {
      props.getCountries();
      if (!mmamaSettings || mmamaSettings.length === 0) {
        props.getMMamaSettings();
      }
      // Fetch rejected drivers and rejection reasons for this patient case
      if (journey?.patientCase?.id) {
        props.getRejectedDriversByPatientCase(journey.patientCase.id);
        if (!rejectionReasonsDriver || rejectionReasonsDriver.length === 0) {
          props.getRejectionReasonsDriver();
        }
        props.getRejectedAmbulancesByPatientCase(journey.patientCase.id);
        if (!rejectionReasonsAmbulance || rejectionReasonsAmbulance.length === 0) {
          props.getRejectionReasonsAmbulance();
        }
      }
      // Reset drivers loaded flag when modal opens
      setDriversLoaded(false);
    } else {
      // Reset selection when modal closes
      setSelectedDriver(null);
      setSelectedDriverCost(null);
      setSelectedDriverDistance(null);
      setSelectedDriverDuration(null);
      setCalculatingCost(false);
      setDriversLoaded(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (mmamaSettings && mmamaSettings.length > 0 && patientCountryId && isOpen) {
      const settings = mmamaSettings.find(setting => setting.country.id === patientCountryId);
      if (settings) {
        setCountrySettings(settings);
      }
    }
  }, [mmamaSettings, patientCountryId, isOpen]);

  // Toggle between driver types
  const toggleDriverType = (type: JourneyDriverType) => {
    if (driverType !== type) {
      setDriverType(type);
      setSelectedDriver(null);
      setSelectedDriverCost(null);
      setSelectedDriverDistance(null);
      setSelectedDriverDuration(null);
    }
  };

  // Handle save button click
  const handleSave = (driver) => {
    if (driver) {
      onSave(driverType, driver, selectedDriverDistance, selectedDriverDuration, selectedDriverCost);
      toggle();
    }
  };

  // Handle opening search modal
  const handleSearchClick = () => {
    setShowSearchModal(true);
  };

  // Handle closing search modal
  const toggleSearchModal = () => {
    setShowSearchModal(false);
  };

  // Handle driver selection from search modal
  const handleSearchDriverSelect = (searchDriverType: JourneyDriverType, driver: IDriverCompact | IAmbulanceCompact) => {
    if (!driver) {
      console.error('No driver selected from search modal.');
      alert('No driver selected. Please select a driver to proceed.');
      return;
    }
    // fetch driver details from driver compact id
    if (searchDriverType === JourneyDriverType.COMMUNITY) {
      const driverId = driver.id;
      // Call synchronous api to fetch driver details
      driverSuggestionService.getDriver(driverId).then(details => {
        handleDriverSelection(details);
        setDriverType(searchDriverType);
        setShowSearchModal(false);
      }).catch(error => {
        console.error('Error fetching driver details:', error);
        alert('An error occurred while fetching driver details. Please try again.');
      });
    } else {
      const ambulanceId = driver.id;
      ambulanceService.getAmbulance(ambulanceId).then(details => {
        handleDriverSelection(details);
        setDriverType(searchDriverType);
        setShowSearchModal(false);
      }).catch(error => {
        console.error('Error fetching ambulance details:', error);
        alert('An error occurred while fetching ambulance details. Please try again.');
      });
    }
  };

  // Handle opening add driver modal
  const handleAddDriverClick = () => {
    setShowAddDriverModal(true);
  };

  // Handle closing add driver modal
  const toggleAddDriverModal = () => {
    setShowAddDriverModal(false);
  };

  // Handle opening add ambulance modal
  const handleAddAmbulanceClick = () => {
    setShowAddAmbulanceModal(true);
  };

  // Handle closing add ambulance modal
  const toggleAddAmbulanceModal = () => {
    setShowAddAmbulanceModal(false);
  };

  // Handle driver save from add driver modal
  const handleAddDriverSave = (driver: IDriver) => {
    handleDriverSelection(driver);
    setDriverType(JourneyDriverType.COMMUNITY);
    setShowAddDriverModal(false);
  };

  // Handle ambulance save from add ambulance modal
  const handleAddAmbulanceSave = (ambulance: IAmbulance) => {
    handleDriverSelection(ambulance);
    setDriverType(JourneyDriverType.AMBULANCE);
    setShowAddAmbulanceModal(false);
  };

  // Handle driver rejection
  const handleDriverRejection = (driver: IDriver | IAmbulance) => {
    setRejectionDriverId(driver.id);
    setShowRRDriverModal(true);
  };

  // Handle closing rejection modal
  const toggleRRDriverModal = () => {
    setShowRRDriverModal(false);
    setRejectionDriverId('');
  };

  // Handle successful rejection submission
  const handleRejectionSuccess = () => {
    setShowRRDriverModal(false);
    setRejectionDriverId('');
  };

  // Handle ambulance rejection
  const handleAmbulanceRejection = (ambulance: IAmbulance) => {
    setRejectionAmbulanceId(ambulance.id);
    setShowRRAmbulanceModal(true);
  };

  // Handle closing ambulance rejection modal
  const toggleRRAmbulanceModal = () => {
    setShowRRAmbulanceModal(false);
    setRejectionAmbulanceId('');
  };

  // Handle successful ambulance rejection submission
  const handleAmbulanceRejectionSuccess = () => {
    setShowRRAmbulanceModal(false);
    setRejectionAmbulanceId('');
  };

  const loadCommunityDriversAll = async () => {
    setLoadingCommunityDrivers(true);
    try {
      let suggestedDrivers: IDriver[] = [];
      const requiredLevel = 1; // Setting it to default level 1 for community drivers for now

      if (journey.startVillage && journey.endVillage) {
        // Village to Village
        suggestedDrivers = await driverSuggestionService.findCommunityDriversForVillageToVillage(
          journey.startVillage.id,
          journey.endVillage.id,
          requiredLevel
        );
      } else if (journey.startVillage && journey.endHealthcareFacility) {
        // Village to Healthcare Facility
        suggestedDrivers = await driverSuggestionService.findCommunityDriversForVillageToHF(
          journey.startVillage.id,
          requiredLevel
        );
      } else if (journey.startHealthcareFacility && journey.endHealthcareFacility) {
        // Healthcare Facility to Healthcare Facility
        suggestedDrivers = await driverSuggestionService.findCommunityDriversForFacilityToFacility(
          journey.startHealthcareFacility.id,
          journey.endHealthcareFacility.id,
          requiredLevel
        );
      } else {
        console.error('Invalid journey data: Unable to determine driver selection criteria.');
      }

      setCommunityDrivers(suggestedDrivers);
    } catch (error) {
      console.error('Error loading community drivers:', error);
      setCommunityDrivers([]);
    } finally {
      setLoadingCommunityDrivers(false);
    }
  };

  /**
   * Load all ambulance drivers synchronously using the service
   */
  const loadAmbulanceDriversAll = async () => {
    setLoadingAmbulanceDrivers(true);
    try {
      let startFacilityId = null;
      let endFacilityId = null;

      const route = journey.route;
      if (route) {
        startFacilityId = route.startFacility?.id || null;
        endFacilityId = route.endFacility?.id || null;
      } else {
        startFacilityId = journey.startHealthcareFacility?.id || null;
        endFacilityId = journey.endHealthcareFacility?.id || null;
      }

      if (!startFacilityId && !endFacilityId) {
        // No suggestions for Village to Village journeys
        setLoadingAmbulanceDrivers(false);
        return;
      }

      const requiredLevel = getRequiredVehicleLevel(
        journey.patientCase?.motherSymptoms || [],
        journey.patientCase?.babySymptoms || [],
      );

      const homeDestinationAmbulances = endFacilityId
        ? await ambulanceService.getAmbulanceDriversForFacilityVehicleRequiredLevel(endFacilityId, requiredLevel)
        : [];
      const homePickupAmbulances = startFacilityId
        ? await ambulanceService.getAmbulanceDriversForFacilityVehicleRequiredLevel(startFacilityId, requiredLevel)
        : [];
      const neighborDestinationAmbulances = endFacilityId
        ? await ambulanceService.getAmbulanceDriversForNeighborFacilityVehicleRequiredLevel(endFacilityId, requiredLevel)
        : [];
      const neighborPickupAmbulances = startFacilityId
        ? await ambulanceService.getAmbulanceDriversForNeighborFacilityVehicleRequiredLevel(startFacilityId, requiredLevel)
        : [];

      const orderedAmbulances: IAmbulance[] = [];
      const seenAmbulances = new Set<string>();
      const appendUnique = (ambulances: IAmbulance[]) => {
        ambulances.forEach(ambulance => {
          if (ambulance?.id && !seenAmbulances.has(ambulance.id)) {
            seenAmbulances.add(ambulance.id);
            orderedAmbulances.push(ambulance);
          }
        });
      };

      appendUnique(homeDestinationAmbulances);
      appendUnique(homePickupAmbulances);
      appendUnique(neighborDestinationAmbulances);
      appendUnique(neighborPickupAmbulances);

      setAmbulanceDrivers(orderedAmbulances);
    } catch (error) {
      console.error('Error loading ambulance drivers:', error);
      setAmbulanceDrivers([]);
    } finally {
      setLoadingAmbulanceDrivers(false);
    }
  };

  // Load drivers when component opens or journey/settings change (only once per modal open)
  useEffect(() => {
    if (isOpen && journey && countrySettings && !driversLoaded) {
      setDriversLoaded(true);

      // Load both driver types initially
      const loadDrivers = async () => {
        await Promise.all([
          loadCommunityDriversAll(),
          loadAmbulanceDriversAll()
        ]);
      };

      loadDrivers();
    }
  }, [isOpen, journey, countrySettings, driversLoaded]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Select a Driver
      </ModalHeader>
      <ModalBody>
        {/* Journey information */}
        <div className="alert alert-info mb-3">
          <p className="mb-0"><strong>Patient is in need of transport from</strong></p>
          <p className="mb-0">From: {fromLocation || 'Not specified'}</p>
          <p className="mb-0">To: {toLocation || 'Not specified'}</p>
          <p className="mb-1">Are you able to pick up this patient?</p>
        </div>

        {/* Driver type tabs */}
        <div className="mb-3">
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({active: driverType === JourneyDriverType.COMMUNITY})}
                onClick={() => toggleDriverType(JourneyDriverType.COMMUNITY)}
              >
                Community Drivers {communityDrivers.length > 0 ? `(${communityDrivers.length})` : ''}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({active: driverType === JourneyDriverType.AMBULANCE})}
                onClick={() => toggleDriverType(JourneyDriverType.AMBULANCE)}
              >
                Ambulance Drivers {ambulanceDrivers.length > 0 ? `(${ambulanceDrivers.length})` : ''}
              </NavLink>
            </NavItem>
          </Nav>
        </div>

        {/* Driver/Ambulance Lists */}
        <TabContent activeTab={driverType}>
          <TabPane tabId={JourneyDriverType.COMMUNITY}>
            {loadingCommunityDrivers ? (
              <div className="text-center py-4">
                <span className="fa fa-spinner fa-spin fa-2x"></span>
                <p className="mt-2">Loading community drivers...</p>
              </div>
            ) : communityDrivers.length > 0 ? (
              <div className="driver-list" style={{maxHeight: '400px', overflowY: 'auto'}}>
                {communityDrivers.map((driver, idx) => {
                  const isRejected = isDriverRejected(driver.id);
                  const rejectionReason = getDriverRejectionReason(driver.id);

                  return (
                    <Row key={`driver-${driver.id}-${idx}`} className="mb-2">
                      <Col md={12}>
                        <Card
                          className={`${selectedDriver?.id === driver.id ? "border-success bg-light" : ""} ${isRejected ? "border-danger" : ""}`}
                          style={{cursor: isRejected ? 'not-allowed' : 'pointer'}}
                          onClick={() => !isRejected && handleDriverSelection(driver)}
                        >
                          <CardBody>
                            {selectedDriver?.id === driver.id && !isRejected && (
                              <div className="text-right">
                                <FontAwesomeIcon icon="check-circle" className="text-success" size="lg"/>
                              </div>
                            )}
                            <div className="ml-3">
                              <p className="mb-0">
                                <FontAwesomeIcon icon="car" className="mr-2"/>
                                {driver.firstName} {driver.lastName}
                                {isRejected && (
                                  <span className="badge badge-danger dispatcher-badge float-right">REJECTED</span>
                                )}
                              </p>
                              <p className="mb-0">
                                <FontAwesomeIcon icon="phone-alt" className="mr-2"/>
                                {driver.phoneNumber}
                              </p>
                              <p className="mb-0">
                                <FontAwesomeIcon icon="map-marker-alt" className="mr-2"/>
                                {driver.homeVillage ? `${driver.homeVillage?.name} / ${driver.homeVillage?.council?.name} / ${driver.homeVillage?.council?.parent?.name}` : 'Unknown Village'}
                              </p>
                              <p className="mb-0">
                                <img className="mr-2"
                                     src={`data:image/svg+xml;base64,${decompressImage(driver.vehicleType?.image || '')}`}
                                     alt="vehicle_image" width={25} height={25}/>
                                {driver.vehicleType ? driver.vehicleType.enType : 'Unknown Vehicle Type'}
                              </p>
                            </div>

                            {isRejected && (
                              <div className="mt-2">
                                <div className="alert alert-warning mb-0">
                                  <FontAwesomeIcon icon="exclamation-triangle" className="mr-2"/>
                                  <strong>Rejection Reason:</strong> {rejectionReason}
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                      {!isRejected && (
                        <Col md="12">
                          <Row>
                            <Col xs="12" md="6" className="mb-2 mb-md-0">
                              <Button
                                color="danger"
                                outline
                                onClick={() => handleDriverRejection({id: driver.id})}
                                className="px-4 w-100"
                              >
                                UNABLE TO ACCEPT PATIENT
                              </Button>
                            </Col>
                            <Col xs="12" md="6">
                              <Button
                                color={selectedDriver?.id === driver.id ? "success" : "primary"}
                                outline={selectedDriver?.id !== driver.id}
                                onClick={() => handleDriverSelection(driver)}
                                className="px-4 w-100"
                              >
                                {selectedDriver?.id === driver.id ? "SELECTED" : "SELECT"}
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <p>There are no community drivers for this pickup location.</p>
              </div>
            )}
          </TabPane>

          <TabPane tabId={JourneyDriverType.AMBULANCE}>
            {loadingAmbulanceDrivers ? (
              <div className="text-center py-4">
                <span className="fa fa-spinner fa-spin fa-2x"></span>
                <p className="mt-2">Loading ambulance drivers...</p>
              </div>
            ) : ambulanceDrivers.length > 0 ? (
              <div className="ambulance-list" style={{maxHeight: '400px', overflowY: 'auto'}}>
                {ambulanceDrivers.map((ambulance, idx) => {
                  const isRejected = isAmbulanceRejected(ambulance.id);
                  const rejectionReason = getAmbulanceRejectionReason(ambulance.id);

                  return (
                    <Row key={`ambulance-${ambulance.id}-${idx}`} className="mb-2">
                      <Col md={12}>
                        <Card
                          className={`${selectedDriver?.id === ambulance.id ? "border-success bg-light" : ""} ${isRejected ? "border-danger" : ""}`}
                          style={{cursor: isRejected ? 'not-allowed' : 'pointer'}}
                          onClick={() => !isRejected && handleDriverSelection(ambulance)}
                        >
                          <CardBody>
                            {selectedDriver?.id === ambulance.id && !isRejected && (
                              <div className="text-right">
                                <FontAwesomeIcon icon="check-circle" className="text-success" size="lg"/>
                              </div>
                            )}
                            <Row>
                              <Col md={12}>
                                <p className="mb-1">
                                  <FontAwesomeIcon icon="ambulance" className="mr-2"/>
                                  {ambulance.driverFirstName} {ambulance.driverLastName}
                                  {isRejected && (
                                    <span className="badge badge-danger dispatcher-badge float-right">REJECTED</span>
                                  )}
                                </p>
                                <p className="mb-0">
                                  <FontAwesomeIcon icon="phone-alt" className="mr-2"/>
                                  {ambulance.phoneNumber}
                                </p>
                                <p className="mb-0">
                                  <FontAwesomeIcon icon="map-marker-alt" className="mr-2"/>
                                  {ambulance.healthcareFacility ? `${ambulance.healthcareFacility?.name} / ${ambulance.healthcareFacility?.council?.name} / ${ambulance.healthcareFacility?.council?.parent?.name}` : 'Unknown Healthcare Facility'}
                                </p>
                                <p className="mb-0">
                                  <img className="mr-2"
                                       src={`data:image/svg+xml;base64,${decompressImage(ambulance.vehicleType?.image || '')}`}
                                       alt="vehicle_image" width={40} height={40}/>
                                  {ambulance.vehicleType ? ambulance.vehicleType.enType : 'Unknown Vehicle Type'}
                                </p>

                                {isRejected && (
                                  <div className="mt-2">
                                    <div className="alert alert-warning mb-0">
                                      <FontAwesomeIcon icon="exclamation-triangle" className="mr-2"/>
                                      <strong>Rejection Reason:</strong> {rejectionReason}
                                    </div>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>
                      {!isRejected && (
                        <Col md="12">
                          <Row>
                            <Col xs="12" md="6" className="mb-2 mb-md-0">
                              <Button
                                color="danger"
                                outline
                                onClick={() => handleAmbulanceRejection({id: ambulance.id})}
                                className="px-4 w-100"
                              >
                                UNABLE TO ACCEPT PATIENT
                              </Button>
                            </Col>
                            <Col xs="12" md="6">
                              <Button
                                color={selectedDriver?.id === ambulance.id ? "success" : "primary"}
                                outline={selectedDriver?.id !== ambulance.id}
                                onClick={() => handleDriverSelection(ambulance)}
                                className="px-4 w-100"
                              >
                                {selectedDriver?.id === ambulance.id ? "SELECTED" : "SELECT"}
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <p>There are no ambulance drivers for this pickup location.</p>
              </div>
            )}
          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter className="d-block">
        {/* Selected Driver Cost Display */}
        {selectedDriver && (
          <Row className="mr-0 ml-0 mb-3">
            <Col md="12">
              <div className="alert alert-success">
                <h5>Selected {driverType === JourneyDriverType.COMMUNITY
                  ? `Community Driver: ${(selectedDriver as IDriver).firstName} ${(selectedDriver as IDriver).lastName}`
                  : `Ambulance Driver: ${(selectedDriver as IAmbulance).driverFirstName} ${(selectedDriver as IAmbulance).driverLastName}`
                }</h5>
                {selectedDriver?.vehicleType?.paymentRequired && (
                  <p className="mb-0">
                    <strong>Estimated Cost: {getSelectedDriverCostDisplay()}</strong>
                  </p>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Add Driver/Ambulance Button */}
        <Row className="mr-0 ml-0">
          <Col md="8" className="text-left">
            <Button color="primary" onClick={handleSearchClick} className="mr-2">
              {driverType === JourneyDriverType.COMMUNITY ? 'SEARCH FOR DRIVER' : 'SEARCH FOR AN AMBULANCE'}
            </Button>
            {driverType === JourneyDriverType.COMMUNITY ? (
              <Button color="secondary" onClick={handleAddDriverClick}>
                ADD NEW DRIVER
              </Button>
            ) : (
              <Button color="secondary" onClick={handleAddAmbulanceClick}>
                ADD NEW AMBULANCE
              </Button>
            )}
          </Col>
          <Col md="4" className="text-right">
            <Button color="danger" onClick={toggle}>
              CANCEL
            </Button>
            <Button color="success" onClick={() => handleSave(selectedDriver)} disabled={!selectedDriver}
                    className="ml-2">
              SAVE
            </Button>
          </Col>
        </Row>
      </ModalFooter>

      {/* Search Driver Modal */}
      <SearchDriverModal
        isOpen={showSearchModal}
        toggle={toggleSearchModal}
        onSave={handleSearchDriverSelect}
        journey={journey}
        driverType={driverType}
      />

      {/* Add Driver Modal */}
      <AddDriverModal
        isOpen={showAddDriverModal}
        toggle={toggleAddDriverModal}
        onSave={handleAddDriverSave}
        countryId={patientCountryId}
      />

      {/* Add Ambulance Modal */}
      <AddAmbulanceModal
        isOpen={showAddAmbulanceModal}
        toggle={toggleAddAmbulanceModal}
        onSave={handleAddAmbulanceSave}
        countryId={patientCountryId}
      />

      {/* RR Driver Rejection Modal */}
      <RRDriverModal
        isOpen={showRRDriverModal}
        toggle={toggleRRDriverModal}
        patientCaseId={journey?.patientCase?.id || ''}
        driverId={rejectionDriverId}
        onSuccess={handleRejectionSuccess}
      />

      {/* RR Ambulance Rejection Modal */}
      <RRAmbulanceModal
        isOpen={showRRAmbulanceModal}
        toggle={toggleRRAmbulanceModal}
        patientCaseId={journey?.patientCase?.id || ''}
        ambulanceId={rejectionAmbulanceId}
        onSuccess={handleAmbulanceRejectionSuccess}
      />
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  mmamaSettings: storeState.mmamaSettings.settings,
  rejectedDrivers: storeState.patientCaseRRDriver.entities,
  rejectionReasonsDriver: storeState.rejectionReasonDriver.entities,
  rejectedAmbulances: storeState.patientCaseRRAmbulance.entities,
  rejectionReasonsAmbulance: storeState.rejectionReasonAmbulance.entities,
});

const mapDispatchToProps = {
  getMMamaSettings,
  getCountries,
  getRejectedDriversByPatientCase,
  getRejectionReasonsDriver,
  getRejectedAmbulancesByPatientCase,
  getRejectionReasonsAmbulance,
  createRoute
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const DriverSelectionModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(DriverSelectionModalComponent);

export default DriverSelectionModal;
